/**
 * Sign-in details for the account currently signed in — never for the other
 * phone's account. Both owner accounts are seeded with a generated password
 * (see backend/src/db/seeds/002_owner_accounts.js) and until now there was no
 * way to move off it from inside the app.
 *
 * Both forms ask for the current password. Changing either of these is a way
 * to lock the real owner out of their own account, and the threat that
 * actually matters here is a phone left unlocked on the counter — not a
 * remote attacker who already has the password.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, UserCog } from 'lucide-react';
import { api, ApiClientError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { TextField } from '../../components/ui/Field.jsx';
import { Button } from '../../components/ui/Button.jsx';

/** Pulls the field-level errors the API returns out of an ApiClientError, falling back to a whole-form message. */
function splitError(err, fallback) {
  if (err instanceof ApiClientError) {
    return { fieldErrors: err.details ?? {}, formError: err.details ? null : err.message };
  }
  return { fieldErrors: {}, formError: fallback };
}

function UsernameForm() {
  const { user, applyUser } = useAuth();
  const [newUsername, setNewUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post('/auth/change-username', { newUsername: newUsername.trim(), currentPassword }),
    onSuccess: (updated) => {
      // The session survives a username change — the token identifies the
      // account by id — so there is nothing to do here but redraw.
      applyUser(updated);
      setNewUsername('');
      setCurrentPassword('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => {
      const { fieldErrors: fe, formError: msg } = splitError(err, 'Could not change your username. Please try again.');
      setFieldErrors(fe);
      setFormError(msg);
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    setSaved(false);
    mutation.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold">Username</h3>
        <p className="text-sm text-muted">
          You sign in as <span className="font-medium text-content">{user?.username}</span>.
        </p>
      </div>

      <TextField
        label="New username"
        required
        value={newUsername}
        onChange={(e) => setNewUsername(e.target.value)}
        error={fieldErrors.newUsername}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck="false"
        hint="Letters, numbers, dots, underscores or hyphens."
      />
      <TextField
        label="Current password"
        required
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        error={fieldErrors.currentPassword}
        autoComplete="current-password"
      />

      {formError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {formError}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button type="submit" variant="secondary" loading={mutation.isPending} disabled={!newUsername.trim() || !currentPassword}>
          Change username
        </Button>
        {saved ? (
          <span className="flex items-center gap-1.5 text-sm text-success">
            <CheckCircle2 size={16} aria-hidden="true" /> Username changed
          </span>
        ) : null}
      </div>
    </form>
  );
}

function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err) => {
      const { fieldErrors: fe, formError: msg } = splitError(err, 'Could not change your password. Please try again.');
      setFieldErrors(fe);
      setFormError(msg);
    },
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    setSaved(false);
    // Checked here and not on the server: the server never sees this field, it
    // exists only to catch a typo in a box whose contents nobody can read back.
    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'The two passwords do not match' });
      return;
    }
    mutation.mutate();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4 border-t border-border pt-5">
      <h3 className="text-sm font-semibold">Password</h3>

      <TextField
        label="Current password"
        required
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        error={fieldErrors.currentPassword}
        autoComplete="current-password"
      />
      <TextField
        label="New password"
        required
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        error={fieldErrors.newPassword}
        autoComplete="new-password"
        hint="At least 8 characters."
      />
      <TextField
        label="Confirm new password"
        required
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={fieldErrors.confirmPassword}
        autoComplete="new-password"
      />

      {formError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {formError}
        </p>
      ) : null}

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant="secondary"
          loading={mutation.isPending}
          disabled={!currentPassword || !newPassword || !confirmPassword}
        >
          Change password
        </Button>
        {saved ? (
          <span className="flex items-center gap-1.5 text-sm text-success">
            <CheckCircle2 size={16} aria-hidden="true" /> Password changed
          </span>
        ) : null}
      </div>
    </form>
  );
}

export function AccountSection() {
  const { user } = useAuth();

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3">
        <UserCog size={20} className="mt-0.5 shrink-0 text-muted" aria-hidden="true" />
        <div>
          <h2 className="text-sm font-semibold">Your sign-in details</h2>
          <p className="text-sm text-muted">
            Applies to this account only{user?.deviceLabel ? ` (${user.deviceLabel})` : ''}. The other phone changes
            its own from its own settings.
          </p>
        </div>
      </div>

      <UsernameForm />
      <PasswordForm />
    </div>
  );
}

export default AccountSection;
